import { startTransition, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';

import { getDocumentOntologyCatalog } from '@/services/database/documentIntelligence';
import {
    buildTextDocumentIntelligence,
    EMPTY_DOCUMENT_INTELLIGENCE,
    type DocumentIntelligence,
} from '@/shared/components/attachments/AttachmentRedactionEditor/documentIntelligence';

interface TranscriptOntologyState {
    error?: string;
    intelligence: DocumentIntelligence;
    text: string;
}

const useTranscriptOntology = ({ enabled, text }: { enabled: boolean; text: string }) => {
    const [activeReference, setActiveReference] = useState<{ id: string; text: string }>();
    const [state, setState] = useState<TranscriptOntologyState>({
        intelligence: EMPTY_DOCUMENT_INTELLIGENCE,
        text: '',
    });

    useEffect(() => {
        if (!enabled || !text.trim() || state.text === text) {
            return;
        }

        let active = true;

        void getDocumentOntologyCatalog()
            .then((ontology) => buildTextDocumentIntelligence(text, ontology))
            .then((intelligence) => {
                if (!active) {
                    return;
                }

                startTransition(() => setState({ intelligence, text }));
            })
            .catch((error: unknown) => {
                if (!active) {
                    return;
                }

                captureException(error);
                setState({
                    error: 'Transcript concepts could not be loaded from the workspace ontology.',
                    intelligence: EMPTY_DOCUMENT_INTELLIGENCE,
                    text,
                });
            });

        return () => {
            active = false;
        };
    }, [enabled, state.text, text]);

    const setActiveReferenceId = useCallback(
        (id: string | undefined) => setActiveReference(id ? { id, text } : undefined),
        [text],
    );
    const resultCurrent = state.text === text;

    return {
        activeReferenceId: activeReference?.text === text ? activeReference.id : undefined,
        error: resultCurrent ? state.error : undefined,
        intelligence: resultCurrent ? state.intelligence : EMPTY_DOCUMENT_INTELLIGENCE,
        loading: Boolean(enabled && text.trim() && !resultCurrent),
        setActiveReferenceId,
    };
};

export default useTranscriptOntology;
